import styled, { css } from 'styled-components';
import { palette } from 'styled-tools';

import { PaletteColor } from '../../components/ThemeProvider';
import { minWidth } from '../../utils/mediaQueries';
import { FontSize, fontSize, FontSizes, FontWeight, fontWeight, lineHeight } from '../../utils/typography';
import { TextAlignment, TextElement, TextElements, TextVerticalAlignment } from './types';

const DefaultNoMarginElements: readonly TextElement[] = [
	TextElements.Div,
	TextElements.Span,
	TextElements.Label,
] as const;

export const getDefaultSize = (element: TextElement): FontSize | undefined => {
	switch (element) {
		case TextElements.H1:
			return FontSizes['4xl'];
		case TextElements.H2:
			return FontSizes['3xl'];
		case TextElements.H3:
			return FontSizes['2xl'];
		case TextElements.H4:
			return FontSizes.xl;
		case TextElements.H5:
			return FontSizes.lg;
		case TextElements.H6:
			return FontSizes.sm;
		case TextElements.P:
		case TextElements.Label:
		case TextElements.Div:
		case TextElements.Span:
			return FontSizes.base;
		default:
			return undefined;
	}
};

export const StyledText = styled.span<{
	as: TextElement;
	$size?: FontSize;
	$weight?: FontWeight;
	$align?: TextAlignment;
	$verticalAlign?: TextVerticalAlignment;
	$color?: PaletteColor;
	$opacity?: number;
	$lineHeight?: number | string;
	$letterSpacing?: string;
	$uppercase?: boolean;
	$noMargin?: boolean;
	$styleAs?: TextElement;
	$srOnly?: boolean;
}>`
	${props => {
		const size = props.$size ?? (props.$styleAs && getDefaultSize(props.$styleAs));
		return size ? fontSize(size) : '';
	}}
	${props => (props.$weight ? fontWeight(props.$weight) : '')}
  ${props =>
		props.$align
			? css`
					text-align: ${props.$align};
			  `
			: ''}
  ${props =>
		props.$verticalAlign
			? css`
					vertical-align: ${props.$verticalAlign};
			  `
			: ''}
  ${props =>
		props.$color
			? css`
					color: ${palette(props.$color)};
			  `
			: ''}
  ${props =>
		props.$opacity
			? css`
					opacity: ${props.$opacity};
			  `
			: ''}
  ${props => {
		if (props.$lineHeight) {
			return `line-height: ${props.$lineHeight};`;
		}
		const size = props.$size ?? getDefaultSize(props.$styleAs ?? props.as);
		if (size) {
			return css`
				${lineHeight(size)};
			`;
		}
		return '';
	}}
  ${props =>
		props.$letterSpacing
			? css`
					letter-spacing: ${props.$letterSpacing};
			  `
			: ''}
  ${props =>
		props.$uppercase
			? css`
					text-transform: uppercase;
			  `
			: ''}
  ${props => {
		return DefaultNoMarginElements.includes(props.as)
			? ''
			: css`
					margin-block-start: 0.5em;
					margin-block-end: 0.5em;
					${minWidth('mobileLarge')} {
						margin-block-start: ${8 / 18}em;
						margin-block-end: ${8 / 18}em;
					}
			  `;
	}}
  ${props =>
		props.$noMargin
			? // We must specify margin: 0 inside the media query for specificity reasons
			  css`
					margin: 0;
					${minWidth('mobileLarge')} {
						margin: 0;
					}
			  `
			: ''}
  ${props =>
		props.$srOnly
			? css`
					position: absolute;
					left: -10000px;
					top: auto;
					width: 1px;
					height: 1px;
					overflow: hidden;
			  `
			: ''}
`;
